(function () {
  'use strict';

  /* ==================================================================
   * THE WELL GUIDE
   * Pathway matcher — educational result.
   * Inputs: 3-question questionnaire (feeling, energy, body).
   * Outputs: pathway match (Reset / Revitalise / Deepen) + an EDUCATIONAL
   *          result (what the path means, supportive facts, ONE recommended
   *          membership, an email-my-plan capture). No prices, no tiers.
   * Updated 2026-06-11: result rebuilt to educate, not sell (issue #64).
   * ================================================================== */

  /* ---------- Pathway page links ---------- */
  var PATHWAY_PAGES = {
    reset: '/pages/reset',
    revitalise: '/pages/revitalise',
    deepen: '/pages/deepen'
  };

  /* ---------- Pathway metadata ---------- */
  var PATHWAYS = {
    reset: {
      name: 'Reset',
      tagline: 'From overwhelm to equilibrium.',
      summary: 'The Reset pathway gives structure to downregulate the nervous system, reduce overwhelm and restore mental and physical energy.',
      idealFor: ['Tired and frazzled', 'High stress, low energy', 'Poor recovery, mental fatigue', 'First time or returning users'],
      outcome: 'Nervous system calm, reduced tension and fatigue, improved sleep and clarity, feeling grounded and stabilised.',
      accent: '#A08878',
      practices: {
        classes: 'Restorative, Breathwork, Yin or Hatha',
        recovery: 'PEMF or red-light treatment'
      }
    },
    revitalise: {
      name: 'Revitalise',
      tagline: 'From disconnection to uplifted.',
      summary: 'Reawaken energy, rebuild momentum and reconnect to joy, movement and vitality.',
      idealFor: ['Low motivation, low mood', 'Emotional flatness', 'Desire for inspiration and connection'],
      outcome: 'Increased energy and motivation, emotional upliftment, renewed sense of connection, feeling alive, engaged and forward-moving.',
      accent: '#A6745B',
      practices: {
        classes: 'Breathwork, Hot Flow, Vinyasa or Inside Flow',
        recovery: 'Body roll or red-light treatment'
      }
    },
    deepen: {
      name: 'Deepen',
      tagline: 'From practice to mastery.',
      summary: 'Build strength, resilience and consistency while refining performance and deepening connection to self.',
      idealFor: ['Established practices and routines', 'Consistent wellness users', 'Performance and growth focused'],
      outcome: 'Increased strength and endurance, improved recovery efficiency, higher physical and mental capacity, deeper connection to practice and purpose.',
      accent: '#3D2E26',
      practices: {
        classes: 'Ashtanga, Iyengar, Inferno Pilates or Strength training',
        recovery: 'Body roll, red-light, compression boots or PEMF'
      }
    }
  };

  /* ---------- Scoring rubric (3 questions only) ---------- */
  var RUBRIC = {
    feeling: {
      frazzled:  { reset: 4, revitalise: 0, deepen: 0 },
      flat:      { reset: 0, revitalise: 3, deepen: 0 },
      motivated: { reset: 0, revitalise: 1, deepen: 3 },
      happy:     { reset: 1, revitalise: 0, deepen: 2 }
    },
    energy: {
      low:     { reset: 2, revitalise: 0, deepen: 0 },
      uneven:  { reset: 1, revitalise: 1, deepen: 0 },
      steady:  { reset: 0, revitalise: 1, deepen: 1 },
      high:    { reset: 0, revitalise: 0, deepen: 2 }
    },
    body: {
      tense: { reset: 2, revitalise: 0, deepen: 0 },
      achy:  { reset: 1, revitalise: 1, deepen: 0 },
      heavy: { reset: 0, revitalise: 2, deepen: 0 },
      ready: { reset: 0, revitalise: 0, deepen: 2 }
    }
  };

  /* ---------- Core matcher ---------- */
  /* Announce funnel progress. assets/analytics.js listens and forwards to
     Google Analytics; nothing here knows or cares about the vendor. */
  function announce(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail: detail || {} }));
  }

  function scorePathway(answers) {
    var scores = { reset: 0, revitalise: 0, deepen: 0 };
    ['feeling', 'energy', 'body'].forEach(function (q) {
      var v = answers[q];
      if (v && RUBRIC[q][v]) {
        ['reset', 'revitalise', 'deepen'].forEach(function (p) {
          scores[p] += RUBRIC[q][v][p] || 0;
        });
      }
    });
    return scores;
  }

  function pickPathway(scores) {
    var top = 'reset', max = -1;
    ['reset', 'revitalise', 'deepen'].forEach(function (p) {
      if (scores[p] > max) { max = scores[p]; top = p; }
    });
    return top;
  }

  function whyMatched(answers, pathway) {
    var feelingCopy = {
      frazzled: 'You’re carrying more than your system can process right now.',
      flat: 'You’re asking your system to move, but the spark isn’t there.',
      motivated: 'Your system is open, curious and ready to build.',
      happy: 'Your system is resourced — good time to stay in rhythm.'
    };
    var bodyCopy = {
      tense: 'Your body is holding tension that asks to be released.',
      achy: 'Your body needs both relief and reactivation.',
      heavy: 'Your body signals sluggishness — time to wake it up.',
      ready: 'Your body is calling for intelligent challenge.'
    };
    return [feelingCopy[answers.feeling], bodyCopy[answers.body]].filter(Boolean).join(' ');
  }

  /* ==================================================================
   * UI
   * ================================================================== */
  var section = document.querySelector('[data-guide]');
  if (!section) return;

  var steps = section.querySelectorAll('[data-step]');
  var backBtn = section.querySelector('[data-back]');
  var skipBtn = section.querySelector('[data-skip]');
  var progress = section.querySelector('[data-progress]');
  var resultEl = section.querySelector('[data-result]');
  var heroEl = section.querySelector('.guide__hero');
  var heroEyebrow = section.querySelector('.guide__hero-eyebrow');
  var heroTitle = section.querySelector('.guide__hero-title');
  var heroIntro = section.querySelector('.guide__hero-intro');
  var currentStep = 0;
  var answers = {};

  /* ---- URL state persistence ---- */
  function parseStateFromUrl() {
    var params = new URLSearchParams(window.location.search);
    var state = params.get('s');
    if (!state) return null;
    try { return JSON.parse(decodeURIComponent(atob(state))); } catch (e) { return null; }
  }
  function encodeStateForUrl(ans) {
    return btoa(encodeURIComponent(JSON.stringify(ans)));
  }

  function showStep(idx) {
    steps.forEach(function (s, i) {
      s.classList.toggle('is-active', i === idx);
    });
    currentStep = idx;
    if (progress) {
      progress.querySelectorAll('span').forEach(function (dot, i) {
        dot.classList.toggle('is-filled', i < idx);
        dot.classList.toggle('is-current', i === idx);
      });
    }
    if (backBtn) backBtn.style.visibility = idx === 0 ? 'hidden' : 'visible';
    if (skipBtn) skipBtn.style.display = idx === steps.length ? 'none' : '';
  }

  function finish() {
    var scores = scorePathway(answers);
    var pathway = pickPathway(scores);
    renderResult({ pathway: pathway, scores: scores, answers: answers });
    announce('well:quiz-complete', { pathway: pathway });
    if (window.history && window.history.replaceState) {
      var newUrl = window.location.pathname + '?s=' + encodeStateForUrl(answers);
      window.history.replaceState({}, '', newUrl);
    }
    section.querySelector('[data-questionnaire]').hidden = true;
    resultEl.hidden = false;
    section.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /* ---------- Education facts (supportive, not sales) ---------- */
  function educationFacts(r) {
    var pw = PATHWAYS[r.pathway];
    var p = pw.practices || {};
    var facts = [];

    facts.push({
      label: 'Where you are',
      body: 'This path suits people who are ' + pw.idealFor.slice(0, 2).join(' or ').toLowerCase() +
            '. Knowing that shapes what we’d gently steer you toward first.'
    });

    if (p.classes) {
      facts.push({
        label: 'Classes that help',
        body: p.classes + ' are the practices most aligned with this path — chosen to meet your body where it is rather than push it.'
      });
    }

    if (p.recovery) {
      facts.push({
        label: 'Recovery that supports',
        body: p.recovery + ', alongside self-guided thermal time, help your system absorb the work and recover well between sessions.'
      });
    }

    return facts.slice(0, 3);
  }

  /* ---------- Result rendering ---------- */
  function renderResult(r) {
    var pw = PATHWAYS[r.pathway];
    var html = '';

    if (heroEl) heroEl.classList.add('guide__hero--result');
    if (heroEyebrow) heroEyebrow.textContent = 'your pathway';
    if (heroTitle) {
      heroTitle.textContent = pw.name;
      heroTitle.style.color = pw.accent;
    }
    var why = whyMatched(r.answers, r.pathway);
    if (heroIntro) heroIntro.textContent = pw.summary + (why ? ' ' + why : '');

    html += '<div class="guide-result">';

    // (a) Pathway intro — what this path means
    html += '<div class="guide-result__intro">';
    html += '  <p class="guide-result__intro-lead">' + pw.tagline + '</p>';
    html += '  <p class="guide-result__intro-body">' + pw.summary + '</p>';
    if (why) html += '  <p class="guide-result__why">' + why + '</p>';
    html += '  <p class="guide-result__intro-outcome"><span>What to expect over time:</span> ' + pw.outcome + '</p>';
    html += '  <a class="guide-result__readmore" href="' + (PATHWAY_PAGES[r.pathway] || '/pages/services') + '">read more about the ' + pw.name + ' path →</a>';
    html += '</div>';

    // (b) "Did you know" education cards
    html += '<div class="guide-result__edu">';
    educationFacts(r).forEach(function (f) {
      html += '<div class="guide-result__edu-card">';
      html += '  <h3 class="guide-result__edu-label">' + f.label + '</h3>';
      html += '  <p class="guide-result__edu-body">' + f.body + '</p>';
      html += '</div>';
    });
    html += '</div>';

    // (c) A single membership CTA — no prices, no tiers, no per-pathway rec
    html += '<div class="guide-result__rec">';
    html += '  <div class="guide-result__rec-actions">';
    html += '    <a href="/pages/access" class="guide-result__rec-cta">explore our memberships</a>';
    html += '  </div>';
    html += '</div>';

    // Contact row
    html += '<aside class="guide-result__start">';
    html += '  <h2 class="guide-result__start-heading">Questions? Get in touch.</h2>';
    html += '  <div class="guide-result__contact-row">';
    html += '    <a href="' + whatsAppUrl(r) + '" class="guide-result__contact-btn guide-result__contact-btn--wa" target="_blank" rel="noopener">';
    html += '      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>';
    html += '      WhatsApp us';
    html += '    </a>';
    html += '    <a href="tel:0106340664" class="guide-result__contact-btn">';
    html += '      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/></svg>';
    html += '      Call 010 634 0664';
    html += '    </a>';
    html += '    <a href="mailto:hello@thewell.africa?subject=' + encodeURIComponent('Guide result: ' + pw.name + ' pathway') + '" class="guide-result__contact-btn">';
    html += '      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>';
    html += '      Email us';
    html += '    </a>';
    html += '  </div>';
    html += '</aside>';

    // Utility
    html += '<div class="guide-result__utility">';
    html += '  <button type="button" data-action="share">Share link</button>';
    html += '  <span class="guide-result__utility-sep">·</span>';
    html += '  <button type="button" data-action="print">Print</button>';
    html += '  <span class="guide-result__utility-sep">·</span>';
    html += '  <a href="/pages/guide">Start again</a>';
    html += '</div>';

    html += '</div>';

    resultEl.innerHTML = html;
    wireResultEvents(r);
    injectPrintQR();
  }

  function whatsAppUrl(r) {
    var pw = PATHWAYS[r.pathway];
    var msg = "Hi, I've done The Well Guide and matched with the " + pw.name + " pathway. I'd like to learn more.";
    return 'https://wa.me/27722362725?text=' + encodeURIComponent(msg);
  }

  function wireResultEvents(r) {
    resultEl.querySelectorAll('[data-action]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var action = btn.getAttribute('data-action');
        if (action === 'share') copyShareLink(btn);
        if (action === 'print') window.print();
      });
    });
  }

  function copyShareLink(btn) {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(window.location.href).then(function () {
      var original = btn.textContent;
      btn.textContent = 'Link copied';
      setTimeout(function () { btn.textContent = original; }, 2000);
    });
  }

  function injectPrintQR() {
    if (section.querySelector('[data-print-qr]')) return;
    var qrDiv = document.createElement('div');
    qrDiv.setAttribute('data-print-qr', '');
    qrDiv.className = 'guide-print-qr';
    var bookingUrl = window.location.origin + '/pages/booking';
    var qrSrc = 'https://api.qrserver.com/v1/create-qr-code/?size=200x200&margin=8&data=' + encodeURIComponent(bookingUrl);
    qrDiv.innerHTML = ''
      + '<img src="' + qrSrc + '" width="140" height="140" alt="Scan to book at thewell.africa">'
      + '<p>Scan to book. thewell.africa/pages/booking</p>';
    resultEl.appendChild(qrDiv);
  }

  /* ---- Tile click handlers ---- */
  section.querySelectorAll('[data-tile]').forEach(function (tile) {
    tile.addEventListener('click', function () {
      var field = tile.getAttribute('data-field');
      var value = tile.getAttribute('data-value');
      answers[field] = value;
      announce('well:quiz-start');
      announce('well:quiz-step', { step: currentStep, field: field, value: value });
      section.querySelectorAll('[data-field="' + field + '"]').forEach(function (t) {
        t.classList.toggle('is-selected', t === tile);
      });
      setTimeout(function () {
        if (currentStep < steps.length - 1) {
          showStep(currentStep + 1);
        } else {
          finish();
        }
      }, 180);
    });
  });

  if (backBtn) {
    backBtn.addEventListener('click', function () {
      if (currentStep > 0) showStep(currentStep - 1);
    });
  }
  if (skipBtn) {
    skipBtn.addEventListener('click', function () {
      if (currentStep < steps.length - 1) showStep(currentStep + 1);
      else finish();
    });
  }

  /* ---- Init: check URL state ---- */
  var hydrated = parseStateFromUrl();
  if (hydrated && hydrated.feeling && hydrated.energy && hydrated.body) {
    answers = hydrated;
    section.querySelector('[data-questionnaire]').hidden = true;
    resultEl.hidden = false;
    var scores = scorePathway(answers);
    var pathway = pickPathway(scores);
    renderResult({ pathway: pathway, scores: scores, answers: answers });
  } else {
    showStep(0);
  }
})();
